/**
 * Funciones puras de filtrado - V05.1
 * PTEL - Sistema de Normalización de Coordenadas
 */

import type { NormalizedInfrastructure, InfrastructureType } from '../../../types/infrastructure';
import type { MarkerGroup } from '../../../constants/markerConfig';
import { MARKER_CONFIGS } from '../../../constants/markerConfig';
import type { FilterState, FilterStats } from './types';

/**
 * Filtra infraestructuras según todos los criterios
 */
export function filterInfrastructures(
  data: NormalizedInfrastructure[],
  filters: FilterState
): NormalizedInfrastructure[] {
  return data.filter((infra) => {
    // Filtro por tipo
    if (filters.selectedTypes.length > 0) {
      if (!filters.selectedTypes.includes(infra.type)) return false;
    }
    
    // Filtro por score
    if (infra.score < filters.minScore || infra.score > filters.maxScore) {
      return false;
    }
    
    // Filtro por texto
    if (filters.searchText.trim()) {
      const search = filters.searchText.toLowerCase();
      const matchName = infra.name.toLowerCase().includes(search);
      const matchAddress = infra.address?.toLowerCase().includes(search) ?? false;
      const matchMuni = infra.municipality?.toLowerCase().includes(search) ?? false;
      if (!matchName && !matchAddress && !matchMuni) return false;
    }
    
    return true;
  });
}

/**
 * Calcula estadísticas de filtrado
 */
export function calculateFilterStats(
  allData: NormalizedInfrastructure[],
  filteredData: NormalizedInfrastructure[]
): FilterStats {
  const byType: Partial<Record<InfrastructureType, number>> = {};
  
  for (const infra of filteredData) {
    byType[infra.type] = (byType[infra.type] || 0) + 1;
  }
  
  const scores = filteredData.map(d => d.score);
  const scoreRange = scores.length > 0
    ? {
        min: Math.min(...scores),
        max: Math.max(...scores),
        avg: Math.round(scores.reduce((a, b) => a + b, 0) / scores.length),
      }
    : { min: 0, max: 0, avg: 0 };
  
  return {
    total: allData.length,
    filtered: filteredData.length,
    byType,
    scoreRange,
  };
}

/**
 * Obtiene tipos de infraestructura de un grupo
 */
export function getTypesInGroup(group: MarkerGroup): InfrastructureType[] {
  return Object.values(MARKER_CONFIGS)
    .filter(config => config.group === group)
    .map(config => config.type);
}

/**
 * Cuenta infraestructuras por tipo (sin filtrar)
 */
export function countByTypeUnfiltered(
  data: NormalizedInfrastructure[]
): Partial<Record<InfrastructureType, number>> {
  const counts: Partial<Record<InfrastructureType, number>> = {};
  for (const infra of data) {
    counts[infra.type] = (counts[infra.type] || 0) + 1;
  }
  return counts;
}
